import { CustomDatePicker } from "@/components/custom-date-picker";
import CustomTable from "@/components/CustomTable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { useAuthState } from "@/context";
import { formatType, parseDMY } from "@/lib/helper";
import { cn } from "@/lib/utils";
import {
  getTotalTransactions,
  getTotalTransactionsCount,
  getTransactionsRate,
} from "@/services/Home.service";
import { getAllTransaction } from "@/services/Transactions.service";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useState } from "react";
import { IoFunnelOutline } from "react-icons/io5";
import { columns } from "./columns";
import AddTransaction from "./common/AddTransaction";
import CustomCard from "@/components/CustomCard";
import ExportFilterAction from "@/components/ExportFilterAction";

const Transactions = () => {
  const q = useQueryClient();
  const [filterTextTrans, setFilterTextTrans] = useState("Today");
  const [currencySymbol] = useState("USD");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [dateTo, setDateTo] = useState()
  const [dateFrom, setDateFrom] = useState()

  const [filter, setFilter] = useState({
    from: "",
    to: "",
    status: "",
    min: "",
    max: "",
    currency: "",
    referral: "",
    search: "",
  });
  const { data, isLoading, refetch } = useInfiniteQuery({
    queryKey: ["transactions", search, page],
    queryFn: async ({ pageParam = page }) =>
      await getAllTransaction(pageParam, filter, search),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length === 0) return undefined;
      return pages.length + 1;
    },
  });

  const transactionSum = useQuery({
    queryKey: ["transactionSum"],
    queryFn: async () => await getTotalTransactions(filter),
  });
  const transactionCount = useQuery({
    queryKey: ["transactionCount"],
    queryFn: async () => await getTotalTransactionsCount(filter),
  });
  const rate = useQuery({
    queryKey: ["rate"],
    queryFn: async () => await getTransactionsRate(filter),
  });

  const dataArr = data?.pages?.map((page) => page?.data?.data?.data).flat();
  const meta = data?.pages?.map((page) => page?.data?.data?.meta).flat()[0];

  const handlePageChange = (e) => {
    setPage(e);
  };

  const apply = () => {
    setPage(1);
    refetch();
    transactionSum?.refetch();
    transactionCount?.refetch();
    rate?.refetch();
  };

  const reset = () => {
    q.clear();
    setFilter({
      from: "",
      to: "",
      status: "",
      min: "",
      max: "",
      currency: "",
      referral: "",
      search: "",
    });
  };

  const { useCurrencies } = useAuthState();
  const currenciesList = useCurrencies();

  const [showFilter, setShowFilter] = useState(false);
  const handleSelectCurrency = (e) => {
    setFilter({
      ...filter,
      currency: e,
    });
  };

  const handleSelectToChange = (dateString) => {
    setDateTo(dateString)
    setFilter({
      ...filter,
      to: formatType(dateString),
    });
  };
  const handleSelectFromChange = (dateString) => {
    setDateFrom(dateString)
    setFilter({
      ...filter,
      from: formatType(dateString),
    });
  };

  const handleSelectStatusChange = (e) => {
    setFilter({
      ...filter,
      status: e,
    });
  };

  const handleMinAndMax = (e, value) => {
    if (e === "min") {
      setFilter({
        ...filter,
        min: value,
      });
    } else {
      setFilter({
        ...filter,
        max: value,
      });
    }
  };

  return (
    <div className="grid py-3 px-6 gap-[40px]">
      <div className="grid gap-6 lg:grid-cols-3">
        <CustomCard
          title="Total Transacted Volume"
          mainTitle={transactionSum?.data?.data?.data?.result?.toLocaleString(
            "en-US",
            {
              style: "currency",
              currency: "USD",
            }
          )}
          text="16"
          isLoading={transactionSum?.isLoading}
          date=""
        />
        <CustomCard
          title="Transaction Count"
          mainTitle={transactionCount?.data?.data?.data?.result}
          text="16"
          date=""
          isLoading={transactionCount?.isLoading}
        />
        <CustomCard
          date=""
          title="Exchange rate"
          mainTitle={`NGN ${rate?.data?.data?.data?.result}/$`}
          isLoading={rate?.isLoading}
          text=""
        />
      </div>

      <Separator />

      <div className="w-full gap-8 grid">
        <div className="flex flex-col gap-4">
          <div className="flex items-center ">
            <Input
              placeholder="Search for transactions by user ID or reference no."
              value={showFilter ? filter?.search : search ?? ""}
              onChange={(event) => {
                showFilter
                  ? setFilter({
                    ...filter,
                    search: event.target.value,
                  })
                  : setSearch(event.target.value);
              }}
              className={cn(
                "max-w-sm ",
                showFilter ? "bg-transparent" : "bg-off-whit-1"
              )}
            />

            <div className="flex gap-4 ml-auto">
              <Button
                variant="outline"
                className="border-off-white text-primary0"
                onClick={() => {
                  setShowFilter(!showFilter);
                  setSearch("");
                }}
              >
                <IoFunnelOutline />
                {showFilter ? "Hide Filter" : "Show Filters"}
              </Button>

              <ExportFilterAction />
              <AddTransaction />
            </div>
          </div>

          <div
            className={cn(
              "flex flex-col gap-2 h-full w-[750px]",
              showFilter ? "block" : "hidden"
            )}
          >
            <div className="flex gap-4 items-center w-full flex-wrap">
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80"> Currency </p>

                <Select
                  onValueChange={handleSelectCurrency}
                  value={filter?.currency}
                >
                  <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-[14px] py-3 border-[1px] ">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {currenciesList?.data?.data?.data?.map((item: any, index: number) => (
                      <SelectItem key={index} value={item?.code}>
                        {item?.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">Start date</p>
                <CustomDatePicker
                  date={dateFrom}
                  setDate={handleSelectFromChange}
                />
              </div>
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">End date</p>
                <CustomDatePicker
                  date={dateTo}
                  setDate={handleSelectToChange}
                />
              </div>
              <div className="flex flex-col gap-2 ">
                <p className="text-xs text-gray-80"> Amount </p>
                <div className="flex gap-4">
                  <div className="flex border-[1px] border-[#EAE6EA] h-10 justify-center items-center gap-1 pr-2 rounded-[8px]">
                    <p className="w-[74px] h-full bg-[#EFEFEF] flex items-center justify-center text-gray-80 text-xs font-medium rounded-s-[8px]">
                      Max
                    </p>
                    <Input
                      type="text"
                      placeholder="0.00"
                      value={filter?.max ?? ""}
                      className="border-none outline-none bg-transparent w-fit h-fit p-0 rounded-none"
                      onChange={(e) => handleMinAndMax("max", e?.target?.value)}
                    />
                  </div>
                  <div className="flex border-[1px] border-[#EAE6EA] h-10 justify-center items-center gap-1 pr-2 rounded-[8px]">
                    <p className="w-[74px] h-full bg-[#EFEFEF] flex items-center justify-center text-gray-80 text-xs font-medium rounded-s-[8px]">
                      Min
                    </p>
                    <Input
                      type="text"
                      placeholder="0.00"
                      value={filter?.min ?? ""}
                      className="border-none outline-none bg-transparent w-fit h-fit p-0 rounded-none"
                      onChange={(e) => handleMinAndMax("min", e?.target?.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 w-[234px]">
                <p className="text-xs text-gray-80">Status</p>

                <Select
                  onValueChange={handleSelectStatusChange}
                  value={filter?.status}
                >
                  <SelectTrigger className="h-10 border-grey-300 rounded-[8px] px-[14px] py-3 border-[1px] ">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent className="w-full">
                    {[
                      {
                        name: "Failed",
                        value: "failed",
                      },
                      {
                        name: "Pending",
                        value: "pending",
                      },
                      {
                        name: "Successful",
                        value: "success",
                      },
                    ]?.map((item, index) => (
                      <SelectItem key={item?.name + index} value={item?.value}>
                        {item?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-4 mt-4">
              <Button onClick={apply} className=" text-sm font-medium ">
                Apply
              </Button>
              <Button
                variant="outline"
                type="button"
                className=" text-sm font-medium text-primary"
                onClick={reset}
              >
                Reset filters
              </Button>
            </div>
          </div>
        </div>

        <CustomTable
          columns={columns}
          data={dataArr ?? []}
          handlePageChange={handlePageChange}
          meta={meta}
          showPagination
          isLoading={isLoading}
        />
      </div>
    </div>
  );
};

export default Transactions;
